import { useCallback, useState } from "react";
import type { ReactNode } from "react";

import {
  CheckCircle2,
  Loader2,
  Pencil,
  RotateCcw,
  UserPlus,
} from "lucide-react";

import {
  normalizeFrenchPhoneCandidate,
  SEARCH_EMAIL_REGEX,
  SEARCH_FRENCH_PHONE_REGEX,
  SEARCH_NAME_REGEX,
} from "@/components/backoffice/cat/vehicle-assignment/client-step/client-search-validation";
import { IndividualEditForm } from "@/components/backoffice/cat/vehicle-assignment/client-step/individual-edit-form";
import {
  buildProfileDisplayLines,
  profileDisplayGridClassName,
  profileDisplayValueClassName,
} from "@/components/backoffice/cat/vehicle-assignment/client-step/party-profile-fields";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import type { ParticulierSearchOutcome } from "@/lib/cat/client-step-contracts";
import {
  individualToEditableWithIdentity,
  isIndividualEditComplete,
} from "@/lib/cat/draft-client-mapper";
import type { EditableIndividualWithIdentity } from "@/lib/cat/individual-profile.types";
import type { RetailIndividual } from "@/lib/cat/retail-client.types";
import { cn } from "@/lib/utils";

type CreationConfig = {
  emptyResultsLabel: string;
  openCreateLabel: string;
  closeCreateLabel: string;
  onCreateCanProceedChange: (v: boolean) => void;
  renderCreateForm: (onCanProceedChange: (v: boolean) => void) => ReactNode;
};

type NoResultsAlertConfig = {
  description: string;
  title: string;
};

type NoInternalCollaboratorAlertConfig = {
  description?: string;
  title: string;
};

function isTrimmedFieldModified(current: string, original: string): boolean {
  return current.trim() !== original.trim();
}

function isProfileModified(
  fiche: EditableIndividualWithIdentity,
  individual: RetailIndividual,
): boolean {
  const original = individualToEditableWithIdentity(individual);
  return (
    isTrimmedFieldModified(fiche.firstName, original.firstName) ||
    isTrimmedFieldModified(fiche.lastName, original.lastName) ||
    isTrimmedFieldModified(fiche.email, original.email) ||
    isTrimmedFieldModified(fiche.addressLine, original.addressLine) ||
    isTrimmedFieldModified(
      fiche.addressFloorApartment,
      original.addressFloorApartment,
    ) ||
    isTrimmedFieldModified(
      fiche.addressBuildingResidence,
      original.addressBuildingResidence,
    ) ||
    isTrimmedFieldModified(fiche.postBox, original.postBox) ||
    isTrimmedFieldModified(fiche.postalCode, original.postalCode) ||
    isTrimmedFieldModified(fiche.city, original.city) ||
    isTrimmedFieldModified(fiche.fixedPhone.value, original.fixedPhone.value) ||
    fiche.fixedPhone.use !== original.fixedPhone.use ||
    isTrimmedFieldModified(
      fiche.mobilePhone.value,
      original.mobilePhone.value,
    ) ||
    fiche.mobilePhone.use !== original.mobilePhone.use
  );
}

function mergedProfileForDisplay(
  individual: RetailIndividual,
  ficheEdit: EditableIndividualWithIdentity | null,
  isSelected: boolean,
): EditableIndividualWithIdentity {
  if (isSelected && ficheEdit) return ficheEdit;
  return individualToEditableWithIdentity(individual);
}

function displayName(
  individual: RetailIndividual,
  ficheEdit: EditableIndividualWithIdentity | null,
  isSelected: boolean,
): string {
  const profile = mergedProfileForDisplay(individual, ficheEdit, isSelected);
  return `${profile.firstName} ${profile.lastName}`.trim();
}

function IndividualsSection({
  additionalDetails,
  creationConfig,
  criteriaLabel = "Critères de recherche",
  editAriaLabelPrefix = "Modifier la fiche de",
  editBadgeLabel = "Modifications en cours",
  emailPlaceholder,
  ficheEdit,
  idPrefix,
  autoOpenCreateForm = false,
  initialShowCreateForm = false,
  isRestoringFromDraft = false,
  isSearchLoading = false,
  newSearchButtonLabel = "Nouvelle recherche",
  noInternalCollaboratorAlert,
  noResultsAlert,
  onFicheChange,
  onResetSearch,
  onSearch,
  onSelectIndividual,
  outcome,
  pEmail,
  pFirstName,
  pLastName,
  pPhone,
  resultsLabel,
  searchButtonLabel,
  selectedBadgeLabel = "Fiche sélectionnée",
  selectedIndividual,
  selectedIndividualId,
  setPEmail,
  setPFirstName,
  setPLastName,
  setPPhone,
}: {
  additionalDetails?: (individual: RetailIndividual) => ReactNode;
  creationConfig?: CreationConfig;
  criteriaLabel?: string;
  editAriaLabelPrefix?: string;
  editBadgeLabel?: string;
  emailPlaceholder: string;
  ficheEdit: EditableIndividualWithIdentity | null;
  idPrefix: string;
  autoOpenCreateForm?: boolean;
  initialShowCreateForm?: boolean;
  isRestoringFromDraft?: boolean;
  isSearchLoading?: boolean;
  newSearchButtonLabel?: string;
  noInternalCollaboratorAlert?: NoInternalCollaboratorAlertConfig;
  noResultsAlert?: NoResultsAlertConfig;
  onFicheChange: (v: EditableIndividualWithIdentity | null) => void;
  onResetSearch?: () => void;
  onSearch: () => void;
  onSelectIndividual: (id: string | null) => void;
  outcome: ParticulierSearchOutcome | null;
  pEmail: string;
  pFirstName: string;
  pLastName: string;
  pPhone: string;
  resultsLabel: (count: number) => string;
  searchButtonLabel: string;
  sectionTitle: string;
  selectedBadgeLabel?: string;
  selectedIndividual: RetailIndividual | null;
  selectedIndividualId: string | null;
  setPEmail: (v: string) => void;
  setPFirstName: (v: string) => void;
  setPLastName: (v: string) => void;
  setPPhone: (v: string) => void;
}) {
  const [editExplicitlyEnabled, setEditExplicitlyEnabled] = useState(false);
  const [editDismissed, setEditDismissed] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(initialShowCreateForm);
  const [createFormDismissed, setCreateFormDismissed] = useState(false);

  const list = outcome?.kind === "list" ? outcome.individuals : [];
  const showServerError = outcome?.kind === "error";
  const showNoInternalCollaborator =
    outcome?.kind === "no_internal_collaborator";
  const showCreate =
    outcome?.kind === "list" && outcome.individuals.length === 0;
  const showList = outcome?.kind === "list" && outcome.individuals.length > 0;
  const showNoCriteria = outcome?.kind === "no_criteria";
  const normalizedLastName = pLastName.trim();
  const normalizedFirstName = pFirstName.trim();
  const hasLastName = normalizedLastName.length > 0;
  const hasFirstName = normalizedFirstName.length > 0;
  const isLastNameValid =
    hasLastName && SEARCH_NAME_REGEX.test(normalizedLastName);
  const isFirstNameValid =
    !hasFirstName || SEARCH_NAME_REGEX.test(normalizedFirstName);
  const hasEmail = pEmail.trim().length > 0;
  const hasPhone = pPhone.trim().length > 0;
  const normalizedPhone = normalizeFrenchPhoneCandidate(pPhone);
  const isEmailValid = !hasEmail || SEARCH_EMAIL_REGEX.test(pEmail.trim());
  const isPhoneValid =
    !hasPhone || SEARCH_FRENCH_PHONE_REGEX.test(normalizedPhone);
  const hasValidEmailOrPhone =
    (hasEmail && isEmailValid) || (hasPhone && isPhoneValid);
  const canSearch =
    isLastNameValid &&
    isFirstNameValid &&
    hasValidEmailOrPhone &&
    isEmailValid &&
    isPhoneValid;

  const incompleteSelectedProfile = Boolean(
    selectedIndividual &&
    ficheEdit &&
    selectedIndividualId === selectedIndividual.id &&
    !isIndividualEditComplete(ficheEdit),
  );

  const isEditEnabled =
    editExplicitlyEnabled || (incompleteSelectedProfile && !editDismissed);

  const handleSelectClient = useCallback(
    (id: string) => {
      setEditExplicitlyEnabled(false);
      setEditDismissed(false);
      onSelectIndividual(id);
    },
    [onSelectIndividual],
  );

  const handleEditClient = useCallback(
    (id: string) => {
      onSelectIndividual(id);
      if (selectedIndividualId === id && isEditEnabled) {
        if (incompleteSelectedProfile) {
          setEditDismissed(true);
        } else {
          setEditExplicitlyEnabled(false);
        }
        return;
      }
      setEditDismissed(false);
      setEditExplicitlyEnabled(true);
    },
    [
      incompleteSelectedProfile,
      isEditEnabled,
      onSelectIndividual,
      selectedIndividualId,
    ],
  );

  const handleSearchClick = useCallback(() => {
    setEditExplicitlyEnabled(false);
    setEditDismissed(false);
    setShowCreateForm(false);
    setCreateFormDismissed(false);
    creationConfig?.onCreateCanProceedChange(false);
    onSearch();
  }, [creationConfig, onSearch]);

  const handleToggleCreateForm = useCallback(() => {
    if (!creationConfig) return;
    setShowCreateForm((prev) => {
      const next = !prev;
      if (next) {
        setCreateFormDismissed(false);
      } else {
        setCreateFormDismissed(true);
        creationConfig.onCreateCanProceedChange(false);
      }
      return next;
    });
  }, [creationConfig]);

  const canShowCreateForm =
    showCreate &&
    !createFormDismissed &&
    (showCreateForm || autoOpenCreateForm);

  const collapseSearchOnSuccess = showList && Boolean(onResetSearch);

  const handleNewSearch = useCallback(() => {
    setEditExplicitlyEnabled(false);
    setEditDismissed(false);
    setShowCreateForm(false);
    setCreateFormDismissed(false);
    creationConfig?.onCreateCanProceedChange(false);
    onResetSearch?.();
  }, [creationConfig, onResetSearch]);

  return (
    <section className="space-y-1">
      <div
        className={cn(
          "flex flex-col gap-3",
          collapseSearchOnSuccess &&
            "flex-row flex-wrap items-center justify-between gap-2",
        )}
      >
        {collapseSearchOnSuccess ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleNewSearch}
          >
            <RotateCcw className="mr-2 size-4" aria-hidden />
            {newSearchButtonLabel}
          </Button>
        ) : null}
      </div>
      <div className="space-y-6">
        {isRestoringFromDraft ? (
          <div
            className="text-muted-foreground flex items-center gap-2 py-4 text-sm"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
            Chargement de la fiche client…
          </div>
        ) : null}

        {!collapseSearchOnSuccess && !isRestoringFromDraft ? (
          <>
            <div className="space-y-2">
              <p className="text-sm font-medium">{criteriaLabel}</p>
              <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-sm">
                <li>Nom obligatoire</li>
                <li>
                  Email ou téléphone obligatoire (au moins l&apos;un des deux)
                </li>
                <li>Prénom optionnel</li>
              </ul>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field data-invalid={!isLastNameValid && hasLastName}>
                <FieldLabel htmlFor={`${idPrefix}-ln`}>
                  Nom <span className="text-destructive">*</span>
                </FieldLabel>
                <FieldContent>
                  <Input
                    id={`${idPrefix}-ln`}
                    value={pLastName}
                    onChange={(e) => setPLastName(e.target.value)}
                    placeholder="Ex: Blanc"
                    type="text"
                    aria-invalid={!isLastNameValid && hasLastName}
                  />
                  {!isLastNameValid && hasLastName ? (
                    <FieldError>
                      Le nom doit contenir au moins 2 lettres.
                    </FieldError>
                  ) : null}
                </FieldContent>
              </Field>
              <Field data-invalid={!isFirstNameValid && hasFirstName}>
                <FieldLabel htmlFor={`${idPrefix}-fn`}>
                  Prénom (optionnel)
                </FieldLabel>
                <FieldContent>
                  <Input
                    id={`${idPrefix}-fn`}
                    value={pFirstName}
                    onChange={(e) => setPFirstName(e.target.value)}
                    placeholder="Ex: Tom"
                    type="text"
                    aria-invalid={!isFirstNameValid && hasFirstName}
                  />
                  {!isFirstNameValid && hasFirstName ? (
                    <FieldError>
                      Le prénom doit contenir au moins 2 lettres.
                    </FieldError>
                  ) : null}
                </FieldContent>
              </Field>
              <Field data-invalid={!isEmailValid}>
                <FieldLabel htmlFor={`${idPrefix}-email`}>
                  Email <span className="text-destructive">*</span>
                  <span className="text-muted-foreground ml-1 text-xs font-normal">
                    ou téléphone
                  </span>
                </FieldLabel>
                <FieldContent>
                  <Input
                    id={`${idPrefix}-email`}
                    type="email"
                    value={pEmail}
                    onChange={(e) => setPEmail(e.target.value)}
                    placeholder={emailPlaceholder}
                    aria-invalid={!isEmailValid}
                  />
                  {!isEmailValid ? (
                    <FieldError>
                      Veuillez saisir une adresse e-mail valide.
                    </FieldError>
                  ) : null}
                </FieldContent>
              </Field>
              <Field>
                <FieldLabel htmlFor={`${idPrefix}-phone`}>
                  Téléphone <span className="text-destructive">*</span>
                  <span className="text-muted-foreground ml-1 text-xs font-normal">
                    ou email
                  </span>
                </FieldLabel>
                <FieldContent>
                  <Input
                    id={`${idPrefix}-phone`}
                    value={pPhone}
                    onChange={(e) => setPPhone(e.target.value)}
                    placeholder="Ex: (+33 ou 0) 611223306"
                    type="tel"
                    aria-invalid={!isPhoneValid}
                  />
                  {!isPhoneValid ? (
                    <FieldError>
                      Veuillez saisir un numéro français valide.
                    </FieldError>
                  ) : null}
                </FieldContent>
              </Field>
            </div>

            <Button
              type="button"
              onClick={handleSearchClick}
              disabled={!canSearch || isSearchLoading}
              aria-disabled={!canSearch || isSearchLoading}
            >
              {isSearchLoading ? "Recherche…" : searchButtonLabel}
            </Button>
          </>
        ) : null}

        {showNoCriteria ? (
          <Alert>
            <AlertDescription>
              Saisissez au moins un critère pour lancer la recherche.
            </AlertDescription>
          </Alert>
        ) : null}

        {showNoInternalCollaborator &&
        outcome?.kind === "no_internal_collaborator" ? (
          <Alert variant="destructive">
            <AlertTitle>
              {noInternalCollaboratorAlert?.title ??
                "Collaborateur groupe introuvable"}
            </AlertTitle>
            <AlertDescription>
              {noInternalCollaboratorAlert?.description ?? outcome.message}
            </AlertDescription>
          </Alert>
        ) : null}

        {showServerError && outcome?.kind === "error" ? (
          <Alert variant="destructive">
            <AlertTitle>Erreur serveur</AlertTitle>
            <AlertDescription>{outcome.message}</AlertDescription>
          </Alert>
        ) : null}

        {showList ? (
          <div className="space-y-3">
            <p className="text-sm font-medium">{resultsLabel(list.length)}</p>
            <p className="text-muted-foreground text-sm">
              Cliquez sur une carte pour la sélectionner.
            </p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {list.map((individual) => {
                const isSelectedCard = selectedIndividualId === individual.id;
                const hasEditableSnapshot =
                  isSelectedCard &&
                  Boolean(ficheEdit) &&
                  selectedIndividual?.id === individual.id;
                const hasEditedValues = Boolean(
                  hasEditableSnapshot &&
                  ficheEdit &&
                  selectedIndividual &&
                  isProfileModified(ficheEdit, selectedIndividual),
                );

                const displayProfile = mergedProfileForDisplay(
                  individual,
                  ficheEdit,
                  isSelectedCard,
                );
                const displayLines = buildProfileDisplayLines(displayProfile);

                return (
                  <Card
                    key={individual.id}
                    className={cn(
                      "cursor-pointer transition-colors",
                      isSelectedCard
                        ? "border-primary bg-primary/5 ring-primary/25 ring-2"
                        : "hover:bg-muted/50 hover:border-primary/40",
                    )}
                    onClick={() => handleSelectClient(individual.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSelectClient(individual.id);
                      }
                    }}
                  >
                    <div className="p-4 text-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1 space-y-3">
                          <div className="font-medium">
                            {displayName(individual, ficheEdit, isSelectedCard)}
                          </div>
                          <div className={profileDisplayGridClassName}>
                            <Field>
                              <FieldLabel>Nom</FieldLabel>
                              <FieldContent>
                                <p className={profileDisplayValueClassName}>
                                  {
                                    mergedProfileForDisplay(
                                      individual,
                                      ficheEdit,
                                      isSelectedCard,
                                    ).lastName
                                  }
                                </p>
                              </FieldContent>
                            </Field>
                            <Field>
                              <FieldLabel>Prénom</FieldLabel>
                              <FieldContent>
                                <p className={profileDisplayValueClassName}>
                                  {
                                    mergedProfileForDisplay(
                                      individual,
                                      ficheEdit,
                                      isSelectedCard,
                                    ).firstName
                                  }
                                </p>
                              </FieldContent>
                            </Field>
                            {additionalDetails?.(individual)}
                            {displayLines.map((line) => (
                              <Field key={line.label}>
                                <FieldLabel>{line.label}</FieldLabel>
                                <FieldContent>
                                  <p className={profileDisplayValueClassName}>
                                    {line.value}
                                  </p>
                                </FieldContent>
                              </Field>
                            ))}
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-2">
                          {isSelectedCard ? (
                            <Badge className="gap-1">
                              <CheckCircle2 className="size-3.5" />
                              {selectedBadgeLabel}
                            </Badge>
                          ) : (
                            <Badge variant="outline">
                              Cliquer pour sélectionner
                            </Badge>
                          )}
                          {hasEditedValues ? (
                            <Badge variant="secondary">{editBadgeLabel}</Badge>
                          ) : null}
                          <Button
                            type="button"
                            size="icon"
                            variant={
                              isSelectedCard && isEditEnabled
                                ? "default"
                                : "outline"
                            }
                            className={
                              isSelectedCard && isEditEnabled
                                ? "border-primary bg-primary text-primary-foreground"
                                : undefined
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClient(individual.id);
                            }}
                            aria-label={`${editAriaLabelPrefix} ${individual.firstName} ${individual.lastName}`}
                          >
                            <Pencil className="size-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        ) : null}

        {selectedIndividual &&
        ficheEdit &&
        !isIndividualEditComplete(ficheEdit) &&
        !isEditEnabled ? (
          <Alert variant="destructive">
            <AlertTitle>Fiche incomplète</AlertTitle>
            <AlertDescription>
              Des champs obligatoires sont manquants ou invalides. Cliquez sur
              le crayon pour compléter la fiche avant de continuer.
            </AlertDescription>
          </Alert>
        ) : null}

        {selectedIndividual && ficheEdit && isEditEnabled ? (
          <IndividualEditForm
            idPrefix={`${idPrefix}-edit`}
            value={ficheEdit}
            onChange={onFicheChange}
          />
        ) : null}

        {showCreate && creationConfig ? (
          <div className="space-y-3">
            <p className="text-sm font-medium">
              {creationConfig.emptyResultsLabel}
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={handleToggleCreateForm}
            >
              <UserPlus className="mr-2 size-4" />
              {showCreateForm
                ? creationConfig.closeCreateLabel
                : creationConfig.openCreateLabel}
            </Button>
            {canShowCreateForm
              ? creationConfig.renderCreateForm(
                  creationConfig.onCreateCanProceedChange,
                )
              : null}
          </div>
        ) : null}

        {showCreate && !creationConfig && noResultsAlert ? (
          <Alert variant="destructive">
            <AlertTitle>{noResultsAlert.title}</AlertTitle>
            <AlertDescription>{noResultsAlert.description}</AlertDescription>
          </Alert>
        ) : null}
      </div>
    </section>
  );
}

export { IndividualsSection };
export type { EditableIndividualWithIdentity, NoResultsAlertConfig };
